import { createContext, ReactNode, useContext, useState, useMemo, useCallback } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import SetGlobalOffer from './SetGlobalOffer';

interface GlobalOfferContextType {
  buyerName: string | null;
  closeModal: () => void;
  isOpen: boolean;
  openModal: (takerMarkets: TakerMarket[], buyerName?: string) => void;
  takerMarkets: TakerMarket[] | null;
}

interface GlobalOfferProviderProps {
  children: ReactNode;
}

const GlobalOfferContext = createContext<GlobalOfferContextType | undefined>(undefined);

export const useGlobalOfferContext = () => {
  const context = useContext(GlobalOfferContext);

  if (!context) {
    throw new Error('useGlobalOfferContext must be used within a GlobalOfferProvider');
  }

  return context;
};

const GlobalOfferProvider = ({ children }: GlobalOfferProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buyerName, setBuyerName] = useState<string | null>(null);
  const [takerMarkets, setTakerMarkets] = useState<TakerMarket[] | null>(null);

  const openModal = useCallback((markets: TakerMarket[], buyerName?: string) => {
    setBuyerName(buyerName ?? null);
    setTakerMarkets(markets);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);

    // clear takerMarkets after modal is closed
    setTimeout(() => {
      setTakerMarkets(null);
    }, 300);
  }, []);

  const contextValue = useMemo(
    () => ({
      buyerName,
      closeModal,
      isOpen,
      openModal,
      takerMarkets,
    }),
    [buyerName, closeModal, isOpen, openModal, takerMarkets]
  );

  return <GlobalOfferContext.Provider value={contextValue}>{children}</GlobalOfferContext.Provider>;
};

const GlobalOfferComponent = () => {
  const { buyerName, closeModal, isOpen, takerMarkets } = useGlobalOfferContext();

  return takerMarkets && takerMarkets.length > 0 ? (
    <SetGlobalOffer buyerName={buyerName} onClose={closeModal} open={isOpen} takerMarkets={takerMarkets} />
  ) : null;
};

export const GlobalOfferWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <GlobalOfferProvider>
      <GlobalOfferComponent />
      {children}
    </GlobalOfferProvider>
  );
};
